import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { EmptyState } from '@/components/empty-state'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type PromptVersionRow,
  useMintPromptVersion,
  usePrompt,
  usePromptPreview,
} from '@/lib/admin-ai'
import { ApiError } from '@/lib/api'
import { formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'
import { BACK_LINK, DetailHeader, JsonBlock, stamp, Tag } from '../-parts'

export const Route = createFileRoute('/_app/admin/ai/prompts/$promptId')({
  component: PromptPage,
})

const FIELD =
  'w-full rounded-md border border-input bg-card px-3 py-2 font-mono text-[12.5px] leading-[1.6] outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40'

/**
 * One slot, its history, and the next wording.
 *
 * A version is never edited. Rewriting one in place would silently redate every
 * earlier answer to a question that was never asked, so the editor mints the
 * next one and the old wording stays exactly where it was — which is what makes
 * a change in an answer readable as a change in the model rather than a change
 * in what we asked it.
 */
function PromptPage() {
  const { promptId } = Route.useParams()
  const { data, isPending, error } = usePrompt(promptId)

  if (error) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          title="Prompt not found"
          description="No prompt slot on this deployment carries that id."
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/admin/ai/prompts">Back to prompts</Link>
            </Button>
          }
        />
      </div>
    )
  }

  if (isPending || !data) return <Skeleton className="h-96 w-full" />

  const { slot, versions } = data
  const active = versions.find((version) => version.active) ?? null

  return (
    <>
      <DetailHeader
        back={
          <Link to="/admin/ai/prompts" className={BACK_LINK}>
            <ArrowLeftIcon className="size-3" aria-hidden />
            Prompts
          </Link>
        }
        title={<span className="font-mono">{slot.key}</span>}
        meta={
          <>
            {slot.organizationId === null ? (
              <Tag>shipped</Tag>
            ) : (
              <span className="text-[12px]">{slot.organization}</span>
            )}
            {slot.host && (
              <span className="font-mono text-[12px] text-muted-foreground">{slot.host}</span>
            )}
            {active && <Tag tone="plain">{`v${active.version}`}</Tag>}
            {slot.purpose && (
              <span className="text-[12px] text-muted-foreground">{slot.purpose}</span>
            )}
          </>
        }
      />

      <div className="space-y-4">
        <Panel title="Versions" meta={formatExact(versions.length)} flush>
          <ul className="divide-y divide-border">
            {versions.map((version) => (
              <Version key={version.id} version={version} />
            ))}
            {versions.length === 0 && (
              <li className="px-4 py-4 text-[13px] text-muted-foreground">
                No wording has been minted yet.
              </li>
            )}
          </ul>
        </Panel>

        <Editor promptId={promptId} active={active} />
      </div>
    </>
  )
}

function Version({ version }: { version: PromptVersionRow }) {
  const [open, setOpen] = useState(version.active)

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5 text-left transition-colors hover:bg-secondary/40"
      >
        <span className="mono-label text-muted-foreground">{open ? '−' : '+'}</span>
        <span className="font-mono text-[12.5px]">{`v${version.version}`}</span>
        {/* Plum is the selected marker, and the version that runs is the one
            the slot has selected. */}
        {version.active && (
          <span className="rounded-[3px] bg-plum-selected px-1.5 py-px font-mono text-[10.5px] text-foreground">
            running
          </span>
        )}
        {version.defaultModelId && (
          <span className="font-mono text-[11.5px] text-muted-foreground">
            {version.defaultModelId}
          </span>
        )}
        <span className="ml-auto flex items-baseline gap-3">
          <span className="font-mono text-[11px] text-muted-foreground tabular">
            {formatExact(version.runs)} runs
          </span>
          <span className="font-mono text-[11px] text-muted-foreground tabular">
            {stamp(version.createdAt)}
          </span>
          {version.createdByName && (
            <span className="text-[11.5px] text-muted-foreground">{version.createdByName}</span>
          )}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-border bg-secondary/20 px-4 py-3">
          {version.notes && <p className="text-[13px]">{version.notes}</p>}

          {version.system && (
            <div className="space-y-1">
              <p className="mono-label text-muted-foreground">System</p>
              <pre className="max-h-[280px] overflow-auto rounded-md border border-border bg-card p-3 font-mono text-[11.5px] leading-[1.65] whitespace-pre-wrap">
                {version.system}
              </pre>
            </div>
          )}

          <div className="space-y-1">
            <p className="mono-label text-muted-foreground">Template</p>
            <pre className="max-h-[400px] overflow-auto rounded-md border border-border bg-card p-3 font-mono text-[11.5px] leading-[1.65] whitespace-pre-wrap">
              {version.template}
            </pre>
          </div>

          {version.placeholders.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {version.placeholders.map((name) => (
                <Tag key={name} tone="plain">
                  {name}
                </Tag>
              ))}
            </div>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            {version.variables && <JsonBlock value={version.variables} className="max-h-[200px]" />}
            {version.params && <JsonBlock value={version.params} className="max-h-[200px]" />}
          </div>
        </div>
      )}
    </li>
  )
}

/**
 * The next version.
 *
 * Preview goes through the api rather than substituting here, because two
 * implementations of one substitution is how a preview comes to differ from the
 * thing it previews — and the whole value of a frozen version is that what ran
 * is what was read. Minting is held behind a preview of the current wording for
 * the same reason: the placeholders the api derived are the ones the version is
 * declared against.
 */
function Editor({ promptId, active }: { promptId: string; active: PromptVersionRow | null }) {
  const [template, setTemplate] = useState(active?.template ?? '')
  const [system, setSystem] = useState(active?.system ?? '')
  const modelFieldId = useId()
  const notesFieldId = useId()
  const [modelId, setModelId] = useState(active?.defaultModelId ?? '')
  const [notes, setNotes] = useState('')
  const [variables, setVariables] = useState<Record<string, string>>(active?.variables ?? {})
  const [values, setValues] = useState<Record<string, string>>({})
  const [previewed, setPreviewed] = useState<string | null>(null)

  const preview = usePromptPreview(promptId)
  const mint = useMintPromptVersion(promptId)

  const placeholders = preview.data?.placeholders ?? []
  const fresh = previewed === template && preview.data !== undefined

  return (
    <Panel
      title="Next version"
      meta={active ? `after v${active.version}` : 'first'}
      actions={
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!template || preview.isPending}
            onClick={() => {
              preview.mutate({ template, values }, { onSuccess: () => setPreviewed(template) })
            }}
          >
            Preview
          </Button>
          <Button
            size="sm"
            disabled={!fresh || mint.isPending}
            onClick={() =>
              mint.mutate(
                {
                  template,
                  ...(system ? { system } : {}),
                  ...(modelId ? { defaultModelId: modelId } : {}),
                  ...(notes ? { notes } : {}),
                  variables: Object.fromEntries(
                    placeholders.map((name) => [name, variables[name] ?? '']),
                  ),
                },
                { onSuccess: () => setNotes('') },
              )
            }
          >
            Mint
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="mono-label block text-muted-foreground">Template</span>
          <textarea
            value={template}
            rows={12}
            spellCheck={false}
            onChange={(event) => setTemplate(event.target.value)}
            className={FIELD}
          />
        </label>

        <label className="block space-y-1">
          <span className="mono-label block text-muted-foreground">System</span>
          <textarea
            value={system}
            rows={4}
            spellCheck={false}
            onChange={(event) => setSystem(event.target.value)}
            className={FIELD}
          />
        </label>

        <div className="flex flex-wrap gap-3">
          <label className="space-y-1" htmlFor={modelFieldId}>
            <span className="mono-label block text-muted-foreground">Default model</span>
            <Input
              id={modelFieldId}
              value={modelId}
              onChange={(event) => setModelId(event.target.value)}
              placeholder="openai/gpt-6-luna"
              className="h-9 w-[260px] font-mono text-[13px]"
            />
          </label>
          <label className="space-y-1" htmlFor={notesFieldId}>
            <span className="mono-label block text-muted-foreground">Notes</span>
            <Input
              id={notesFieldId}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="h-9 w-[360px] text-[13px]"
            />
          </label>
        </div>

        {placeholders.length > 0 && (
          <div className="space-y-2 border-t border-border pt-4">
            <p className="mono-label text-muted-foreground">Placeholders</p>
            <table className="w-full border-collapse text-[13px]">
              <tbody>
                {placeholders.map((name) => {
                  const missing = preview.data?.missing.includes(name) ?? false
                  return (
                    <tr key={name} className="align-middle">
                      <td className="w-[180px] py-1.5 pr-3">
                        <span
                          className={cn('font-mono text-[12px]', missing && 'text-destructive')}
                        >
                          {name}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3">
                        <Input
                          value={variables[name] ?? ''}
                          placeholder="what it means"
                          onChange={(event) =>
                            setVariables((prev) => ({ ...prev, [name]: event.target.value }))
                          }
                          className="h-8 text-[12.5px]"
                        />
                      </td>
                      <td className="w-[320px] py-1.5">
                        <Input
                          value={values[name] ?? ''}
                          placeholder="a value to preview with"
                          onChange={(event) =>
                            setValues((prev) => ({ ...prev, [name]: event.target.value }))
                          }
                          className="h-8 font-mono text-[12.5px]"
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {preview.error && (
          <p className="font-mono text-[12px] text-destructive">
            {preview.error instanceof ApiError ? preview.error.message : 'Could not render that'}
          </p>
        )}

        {mint.error && (
          <p className="font-mono text-[12px] text-destructive">
            {mint.error instanceof ApiError ? mint.error.message : 'Could not mint that version'}
          </p>
        )}

        {preview.data && (
          <div className="space-y-2 border-t border-border pt-4">
            <div className="flex flex-wrap items-center gap-3">
              <p className="mono-label text-muted-foreground">What would be sent</p>
              {/* The run route refuses on exactly this list, so a hole here is a
                  run that would fail rather than a preview that looks odd. */}
              {preview.data.missing.map((name) => (
                <Tag key={name} tone="bad">
                  {`missing ${name}`}
                </Tag>
              ))}
              {!fresh && <Tag tone="warn">template edited since</Tag>}
            </div>
            <pre className="max-h-[400px] overflow-auto rounded-md border border-border bg-secondary/30 p-3 font-mono text-[11.5px] leading-[1.65] whitespace-pre-wrap">
              {preview.data.text}
            </pre>
          </div>
        )}
      </div>
    </Panel>
  )
}
